/**
 * Shared types, constants, and hooks used by both OrderDetailPanel and
 * VisitDetailPanel. Neither panel renders this file directly — they import
 * from it and extend/compose their own UI on top.
 */

import { useMemo } from 'react';
import type { TaskStatus } from '../../services/api/types.gen';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import { getClinicianDisplayName } from '../../utils/clinicianUtils';
import { EDITABLE_STATUSES, SIGNABLE_STATUSES } from '../../constants/statuses';

// ---------------------------------------------------------------------------
// Status display labels
// ---------------------------------------------------------------------------

export const STATUS_DISPLAY_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  submitted_with_signature: 'Submitted (Signed)',
  submitted: 'Submitted',
  pending_clinician_signature: 'Pending Clinician Signature',
  completed: 'Completed',
  exported: 'Exported',
  reopened: 'Reopened',
  sent_to_physician: 'Sent to Physician',
  returned_with_signature: 'Returned with Signature',
  sent_no_signature_required: 'Completed',
};

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export function getStatusBadgeVariant(
  status: TaskStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'completed': // fallthrough
    case 'exported':
      return 'default';
    case 'submitted_with_signature':
    case 'pending_clinician_signature':
      return 'secondary';
    case 'not_started':
    case 'saved':
    case 'submitted':
    case 'reopened':
      return 'outline';
    default:
      return 'outline';
  }
}

export const formatDate = (dateString: string | null | undefined): string => {
  const parsed = parseISOToLocalDate(dateString);
  if (!parsed) return 'N/A';
  return parsed.toLocaleDateString();
};

/**
 * @deprecated Use {@link getClinicianDisplayName} (or {@link cliniciansToSelectOptions}
 * for dropdowns) directly. Kept as a thin alias so existing imports keep working.
 */
export const formatClinicianName = (c: AvailableClinicianResponse): string =>
  getClinicianDisplayName(c);

// ---------------------------------------------------------------------------
// useTaskPermissions hook
//
// Computes role-based permission flags for sign/approve/break actions.
// Both panels have identical logic — centralised here so changes stay in sync.
// ---------------------------------------------------------------------------

interface UserClinic {
  clinic_id: string;
  is_assignable_clinician: boolean;
  is_case_manager: boolean;
}

interface User {
  id: string;
  user_clinics?: UserClinic[];
}

interface TaskLike {
  status: TaskStatus;
  agency_clinic_id?: string | null;
  assigned_clinician_user_id?: string | null;
}

/**
 * Agency opt-in for the ROG-3268 unsigned moves. Passed in rather than read
 * from a store here so this hook stays a pure function of (user, task, config).
 */
export interface UnsignedMoveConfig {
  allowSubmitWithoutSignature?: boolean;
}

export interface TaskPermissions {
  hasClinicianRole: boolean;
  hasCaseManagerRole: boolean;
  canSign: boolean;
  canApprove: boolean;
  canBreakSignatures: boolean;
  canSubmitWithoutSignature: boolean;
  canReturnToClinician: boolean;
}

export function useTaskPermissions(
  user: User | null | undefined,
  task: TaskLike | null | undefined,
  config: UnsignedMoveConfig = {}
): TaskPermissions {
  const allowUnsigned = Boolean(config.allowSubmitWithoutSignature);
  const hasClinicianRole = useMemo(() => {
    if (!user?.user_clinics?.length || !task?.agency_clinic_id) return false;
    return user.user_clinics.some(
      uc => uc.clinic_id === task.agency_clinic_id && uc.is_assignable_clinician
    );
  }, [user?.user_clinics, task?.agency_clinic_id]);

  const hasCaseManagerRole = useMemo(() => {
    if (!user?.user_clinics?.length || !task?.agency_clinic_id) return false;
    return user.user_clinics.some(
      uc => uc.clinic_id === task.agency_clinic_id && uc.is_case_manager
    );
  }, [user?.user_clinics, task?.agency_clinic_id]);

  // The specific clinician the task is assigned to (not merely anyone holding the
  // clinician role at the clinic). The backend gates signing and breaking on this
  // exact identity, so the UI must too.
  const isAssignedClinician = useMemo(
    () =>
      Boolean(
        user?.id &&
          task?.assigned_clinician_user_id &&
          user.id === task.assigned_clinician_user_id
      ),
    [user?.id, task?.assigned_clinician_user_id]
  );

  const canSign = useMemo(() => {
    if (!user?.id || !task) return false;
    if (!SIGNABLE_STATUSES.has(task.status)) return false;
    if (task.status === 'submitted') return hasCaseManagerRole;
    // For clinician slot: user must be the specific assigned clinician
    if (task.assigned_clinician_user_id) {
      return user.id === task.assigned_clinician_user_id;
    }
    return hasClinicianRole;
  }, [
    task?.status,
    task?.assigned_clinician_user_id,
    user?.id,
    hasClinicianRole,
    hasCaseManagerRole,
  ]);

  const canApprove = useMemo(() => {
    if (!user?.id || !task) return false;
    if (task.status !== 'submitted_with_signature') return false;
    return hasCaseManagerRole;
  }, [task?.status, user?.id, hasCaseManagerRole]);

  const canBreakSignatures = useMemo(() => {
    if (!user?.id || !task) return false;
    // Mirrors backend break_task_signatures: the assigned clinician or any case
    // manager at the clinic can unlock from any signed-but-not-complete state
    // (submitted_with_signature / pending_clinician_signature) and can reopen a
    // finished chart (completed / exported).
    if (
      task.status === 'submitted_with_signature' ||
      task.status === 'pending_clinician_signature' ||
      task.status === 'completed' ||
      task.status === 'exported'
    ) {
      return hasCaseManagerRole || isAssignedClinician;
    }
    return false;
  }, [task?.status, user?.id, hasCaseManagerRole, isAssignedClinician]);

  // ROG-3268 — deferrals. Mirrors submit_task_without_signature: the assigned
  // clinician, from a draft status, and only where the agency opted in.
  const canSubmitWithoutSignature = useMemo(() => {
    if (!allowUnsigned || !user?.id || !task) return false;
    if (!EDITABLE_STATUSES.has(task.status)) return false;
    // `submitted` is editable but is already past this door.
    if (task.status === 'submitted') return false;
    return isAssignedClinician;
  }, [allowUnsigned, task?.status, user?.id, isAssignedClinician]);

  // Mirrors return_task_to_clinician: a case manager handing work back rather
  // than approving it.
  const canReturnToClinician = useMemo(() => {
    if (!allowUnsigned || !user?.id || !task) return false;
    if (
      task.status !== 'submitted_with_signature' &&
      task.status !== 'submitted'
    ) {
      return false;
    }
    return hasCaseManagerRole;
  }, [allowUnsigned, task?.status, user?.id, hasCaseManagerRole]);

  return {
    hasClinicianRole,
    hasCaseManagerRole,
    canSign,
    canApprove,
    canBreakSignatures,
    canSubmitWithoutSignature,
    canReturnToClinician,
  };
}
